import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, apiSend, apiFetch, errorMessage } from "./api";
// Mêmes gabarits visuels que la gestion des utilisateurs côté admin
import "./UserManagement.css";
import "./CreateUserModal.css";

function CreateStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (form.password !== form.confirm_password) {
      setError("La confirmation ne correspond pas au mot de passe");
      return;
    }
    setLoading(true);
    try {
      await apiSend("/api/staff", "POST", {
        name: form.name,
        surname: form.surname,
        email: form.email,
        password: form.password,
      }, "Erreur lors de la création");
      onCreated();
      return;
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la création"));
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Créer un sous-compte</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}
          <label>Nom</label>
          <input name="name" value={form.name} onChange={handleChange} required maxLength={50} />
          <label>Prénom</label>
          <input name="surname" value={form.surname} onChange={handleChange} required maxLength={50} />
          <label>Email</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} required />
          <label>Mot de passe</label>
          <input
            name="password" type="password" value={form.password}
            onChange={handleChange} required placeholder="8 caractères minimum"
            autoComplete="new-password"
          />
          <label>Confirmer le mot de passe</label>
          <input
            name="confirm_password" type="password" value={form.confirm_password}
            onChange={handleChange} required autoComplete="new-password"
          />
          <button type="submit" className="modal-submit" disabled={loading}>
            {loading ? "Création..." : "Créer"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StaffManagement({ user, onNavigate, onLogout }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchStaff = useCallback(async () => {
    try {
      const data = await apiGet("/api/staff", "Impossible de charger les sous-comptes");
      setStaff(data);
      setError("");
    } catch (e) {
      setStaff([]);
      setError(errorMessage(e, "Impossible de charger les sous-comptes"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleDelete = async (member) => {
    if (!window.confirm(`Supprimer le sous-compte de ${member.surname} ${member.name} ? Il ne pourra plus se connecter.`)) {
      return;
    }
    setDeletingId(member.id);
    try {
      await apiFetch(`/api/staff/${member.id}`, { method: "DELETE" }, "Erreur lors de la suppression");
      await fetchStaff();
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la suppression"));
    }
    setDeletingId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="um-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Sous-comptes"
        onBack={() => onNavigate("home")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="um-main">
        <div className="um-toolbar">
          <p className="um-count">
            {staff.length} sous-compte{staff.length > 1 ? "s" : ""}
          </p>
          <button className="um-btn-create" onClick={() => setShowCreate(true)}>
            + Créer un sous-compte
          </button>
        </div>

        {error && <div className="um-error">{error}</div>}

        {loading ? (
          <p className="um-loading">Chargement...</p>
        ) : staff.length === 0 ? (
          <p className="um-empty">
            Aucun sous-compte. Créez-en un pour permettre à un employé de
            scanner les cartes et générer les QR de votre commerce.
          </p>
        ) : (
          <>
            <div className="um-table-wrapper">
              <table className="um-table">
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Statut</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <span className="um-client-name">{m.surname} {m.name}</span>
                        <span className="um-client-email" title={m.email}>{m.email}</span>
                      </td>
                      <td><span className={`um-badge um-badge-${m.status.toLowerCase()}`}>{m.status}</span></td>
                      <td>{formatDate(m.date_creation)}</td>
                      <td>
                        <button
                          className="um-btn-edit"
                          onClick={() => handleDelete(m)}
                          disabled={deletingId === m.id}
                        >
                          {deletingId === m.id ? "..." : "Supprimer"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards for mobile */}
            <div className="um-cards">
              {staff.map((m) => (
                <div className="um-card" key={m.id}>
                  <div className="um-card-header">
                    <strong>{m.surname} {m.name}</strong>
                    <span className={`um-badge um-badge-${m.status.toLowerCase()}`}>{m.status}</span>
                  </div>
                  <div className="um-card-row">{m.email}</div>
                  <div className="um-card-footer">
                    <span className="um-card-date">{formatDate(m.date_creation)}</span>
                  </div>
                  <button
                    className="um-btn-edit um-btn-edit-mobile"
                    onClick={() => handleDelete(m)}
                    disabled={deletingId === m.id}
                  >
                    {deletingId === m.id ? "..." : "Supprimer"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {showCreate && (
          <CreateStaffModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); fetchStaff(); }}
          />
        )}
      </main>
    </div>
  );
}

export default StaffManagement;
